import { useLayoutEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import type { Announcement, AnnouncementFeature } from './types';

interface Props { announcement: Announcement; open: boolean; onClose: () => void; }

/**
 * Met en gras les passages `**…**` d'un paragraphe d'intro.
 *
 * Toujours sans dangerouslySetInnerHTML (P1-7) : on découpe la chaîne et on
 * rend des `<strong>` React. Un `**` orphelin reste affiché tel quel, ce qui
 * rend l'erreur de saisie visible dans la preview Storybook.
 */
export function renderEmphasis(text: string) {
  return text.split(/\*\*(.+?)\*\*/g).map((part, i) =>
    i % 2 === 1 ? <strong key={i} className="font-semibold text-charcoal">{part}</strong> : part);
}

/**
 * Corps d'une feature (paragraphes OU sous-points) + CTA optionnel.
 *
 * P1-7 : texte brut, jamais dangerouslySetInnerHTML — le catalogue stocke de
 * VRAIS caractères. Partagé entre la ligne dépliable (`digest`) et le rendu
 * toujours-ouvert (`spotlight`) pour qu'un seul endroit décrive la mise en forme.
 */
function FeatureBody({ feature }: { feature: AnnouncementFeature }) {
  return (
    <>
      {feature.body.kind === 'paragraphs'
        ? feature.body.paragraphs.map((p, i) => (
            <p key={i} className="mb-[7px] last:mb-0">{p}</p>))
        : (<ul className="space-y-0">
            {feature.body.subpoints.map((sp, i) => (
              <li key={i} className="relative pl-[18px] py-[7px] border-t border-grey-100 first:border-0">
                <span className="absolute left-0.5 top-[15px] h-1.5 w-1.5 rounded-full bg-[#B8860B]" />
                <span className="font-semibold text-charcoal block text-[13.5px]">{sp.title}</span>
                <span className="text-grey-600 block mt-0.5 text-[13px] leading-[1.5]">{sp.description}</span>
              </li>))}
          </ul>)}
      {feature.cta && (
        <a href={feature.cta.url} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 mt-[6px] text-[12.5px] font-semibold text-[#1f3d6b] hover:underline">
          &#9658;&nbsp;{feature.cta.label}</a>)}
    </>
  );
}

/**
 * Ligne dépliable d'une feature d'annonce (layout `digest`).
 *
 * Disclosure accessible (RGAA 4.1) : le bouton porte `aria-expanded` +
 * `aria-controls`, le panneau déplié porte l'`id` correspondant.
 */
function FeatureRow({ feature, isOpen, onToggle }: {
  feature: AnnouncementFeature; isOpen: boolean; onToggle: () => void;
}) {
  return (
    <div className="border-b border-grey-100 last:border-0">
      <button onClick={onToggle}
        aria-expanded={isOpen} aria-controls={`pa-${feature.id}`}
        className="w-full flex items-center gap-3.5 py-[9px] text-left group">
        <span className="flex-1 font-serif text-[16px] leading-[1.25] font-semibold text-charcoal">
          {feature.title}</span>
        <span className="flex items-center gap-2 text-grey-600">
          <span className="text-[11.5px] font-semibold text-[#8a6400]">
            {isOpen ? 'Réduire'
              : feature.body.kind === 'subpoints' ? 'Voir plus' : 'Découvrir'}</span>
          <span className={`text-[12px] text-grey-400 transition-transform
            ${isOpen ? 'rotate-90 text-grey-600' : ''}`}>&#10095;</span>
        </span>
      </button>
      {isOpen && (
        <div id={`pa-${feature.id}`} className="pt-[2px] pb-[14px] text-[13.5px] text-grey-800 leading-[1.55]">
          <FeatureBody feature={feature} />
        </div>)}
    </div>);
}

/** Construit la src d'embed selon le provider (autoplay au clic). */
function buildEmbedSrc(video: NonNullable<Announcement['video']>): string {
  switch (video.provider) {
    case 'youtube':
      return `https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0`;
    case 'loom':
      return `https://www.loom.com/embed/${video.id}?autoplay=1`;
    case 'vimeo':
      return `https://player.vimeo.com/video/${video.id}?autoplay=1`;
  }
}

/**
 * Lecteur vidéo intégré, pattern « façade » : on n'injecte l'iframe (et donc le
 * player tiers + ses cookies) qu'au clic sur Lecture. Avant lecture : une
 * vignette légère + bouton. Trois états :
 * - `video` présent → lecture intégrée dans la modale au clic ;
 * - `href` seul (la vidéo vit sur une page, ex. centre d'aide) → la façade
 *   navigue vers cette page DANS L'ONGLET COURANT (navigation interne
 *   app.hector.legal, pas un site tiers — on reste dans l'app) ;
 * - ni l'un ni l'autre → état « à venir » sans iframe — on n'invente pas d'ID.
 */
function VideoEmbed({ video, href }: { video?: Announcement['video']; href?: string }) {
  const [playing, setPlaying] = useState(false);
  const src = video ? buildEmbedSrc(video) : null;

  const facade = (
    <>
      {/* Voile sombre pour lisibilité du bouton sur n'importe quelle vignette */}
      <span className="absolute inset-0 bg-gradient-to-br from-charcoal/70 to-charcoal/40" />
      <span className="relative flex h-[58px] w-[58px] items-center justify-center rounded-full
                       bg-white/95 text-[#1f3d6b] shadow-lg transition-transform group-hover:scale-105">
        <span className="ml-[3px] text-[22px]">&#9658;</span>
      </span>
      <span className="relative text-[12.5px] font-semibold text-white/90">
        {src || href ? 'Voir la vidéo de présentation' : 'Vidéo de présentation : bientôt disponible'}
      </span>
    </>
  );
  const facadeClasses = `group absolute inset-0 flex flex-col items-center justify-center gap-3
                         bg-cover bg-center`;
  const posterStyle = video?.poster ? { backgroundImage: `url(${video.poster})` } : undefined;

  return (
    <div
      className="relative w-full overflow-hidden rounded-[10px] border border-grey-200 bg-charcoal"
      style={{ aspectRatio: '16 / 9' }}
    >
      {playing && src ? (
        <iframe
          src={src}
          title="Vidéo de présentation"
          className="absolute inset-0 h-full w-full"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      ) : src ? (
        <button type="button" onClick={() => setPlaying(true)}
          aria-label="Lire la vidéo de présentation"
          className={facadeClasses} style={posterStyle}>
          {facade}
        </button>
      ) : href ? (
        // Même onglet (pas de target="_blank") : la vidéo vit sur une page
        // d'aide interne à l'app — on y navigue sans ouvrir d'onglet (choix
        // produit). Le rel n'est plus nécessaire sans _blank.
        <a href={href}
          aria-label="Voir la vidéo de présentation"
          className={facadeClasses} style={posterStyle}>
          {facade}
        </a>
      ) : (
        <div aria-label="Vidéo de présentation à venir" className={facadeClasses}>
          {facade}
        </div>
      )}
    </div>
  );
}

/**
 * Contenu d'une annonce : header (kicker/titre/sous-titre) + corps + footer.
 *
 * Extrait de la modale pour être réutilisé par le centre d'annonces
 * (`AnnouncementCenterModal`) comme volet de droite. Monter avec
 * `key={announcement.id}` pour réinitialiser l'état local (lignes dépliées,
 * verrou de hauteur) au changement d'annonce.
 *
 * Deux mises en page (champ `layout`) :
 * - `digest` (défaut) : récap mensuel, lignes dépliables, hauteur verrouillée
 *   à l'état replié (cf. mockup v8/v11 validé).
 * - `spotlight` : mise en avant courte ou lancement d'UNE fonctionnalité —
 *   accroche, bénéfices optionnels tous visibles, vidéo optionnelle, scroll naturel.
 */
export function AnnouncementContent({
  announcement,
  onCtaClick,
}: {
  announcement: Announcement;
  /**
   * Modale login uniquement : le CTA central doit fermer l'annonce comme la
   * croix, sinon `AnnouncementGate` ne marque pas l'annonce vue et elle revient
   * en boucle après navigation vers les réglages.
   */
  onCtaClick?: () => void;
}) {
  const isSpotlight = announcement.layout === 'spotlight';

  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setOpenIds((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return n;
  });

  /**
   * (Layout `digest` uniquement.) Verrouille la zone scrollable sur sa hauteur
   * À L'ÉTAT REPLIÉ : au montage `openIds` est vide → `scrollHeight` = taille de
   * base exacte du sommaire. Replié = snug ; déplié = scroll interne, cadre
   * constant (plus de saut de taille). Re-mesure sur `document.fonts.ready`
   * (Crimson Text peut arriver après le 1er paint). Inutile en `spotlight` :
   * tout est ouvert d'emblée, on laisse le contenu couler dans le plafond 90vh.
   */
  const bodyRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (isSpotlight) return;
    const el = bodyRef.current;
    if (!el) return;
    const lock = () => {
      el.style.height = '';
      const collapsed = el.scrollHeight;
      const cap = Math.max(240, Math.round(window.innerHeight * 0.9) - 150);
      el.style.height = `${Math.min(collapsed, cap)}px`;
    };
    lock();
    document.fonts?.ready.then(lock).catch(() => {});
  }, [announcement, isSpotlight]);

  return (
    <div className="flex flex-col min-h-0 flex-1">
      <header className="px-12 pt-[22px] pb-[14px] border-b border-grey-100">
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#B8860B] mb-[7px]">
          {announcement.kicker}</div>
        <h2 className="text-[22px] font-semibold text-charcoal tracking-tight">{announcement.title}</h2>
        {/* En spotlight, titre + sous-titre forment un titre unifié sur deux
            lignes, À LA MÊME TAILLE : « Hector présente : » / « L'analyse
            avancée du dossier ». Le sous-titre = le nom de la fonctionnalité
            lancée, donc aussi gros que le titre (pas un petit gris italique
            comme le récap digest). */}
        {isSpotlight && announcement.subtitle ? (
          <p className="text-[22px] font-semibold text-charcoal tracking-tight leading-[1.25]">
            {announcement.subtitle}</p>
        ) : !isSpotlight ? (
          <p className="font-serif italic text-[14px] text-grey-600 mt-[3px]">{announcement.subtitle}</p>
        ) : null}
      </header>

      {isSpotlight ? (
        <div className="flex-1 min-h-0 overflow-auto px-12 pt-[14px] pb-[18px]">
          {announcement.intro && announcement.intro.length > 0 && (
            <div className="text-[14px] text-grey-800 leading-[1.6]">
              {announcement.intro.map((p, i) => (
                <p key={i} className="mb-[8px] last:mb-0">{renderEmphasis(p)}</p>))}
            </div>
          )}

          {announcement.introCallout && (
            <div className="my-[18px] flex flex-col items-center gap-3 text-center">
              <p className="font-serif italic text-[15px] leading-[1.45] text-charcoal">
                {announcement.introCallout.text}
              </p>
              {announcement.introCallout.cta && (
                <a
                  href={announcement.introCallout.cta.url}
                  onClick={onCtaClick}
                  className="inline-flex items-center justify-center rounded-[8px] bg-charcoal px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-grey-900"
                >
                  {announcement.introCallout.cta.label}
                </a>
              )}
            </div>
          )}

          {announcement.sections.map((sec, si) => (
            // Une section à une seule feature s'étale sur toute la largeur (pas
            // de colonne vide à droite) et son contenu est centré ; à partir de
            // 2 features, grille 2 colonnes alignée à gauche comme avant.
            <section key={sec.label || si} className={`mt-[18px] ${sec.features.length === 1 ? 'text-center' : ''}`}>
              {/* Label optionnel : une annonce à une seule section (le label
                  ferait doublon avec le titre) peut le laisser vide. */}
              {sec.label && (
                <div className="text-[9.5px] font-bold uppercase tracking-[0.12em] text-grey-400 mb-[8px]">
                  {sec.label}</div>
              )}
              <div className={`grid grid-cols-1 gap-x-8 gap-y-[14px] ${
                sec.features.length > 1 ? 'sm:grid-cols-2' : ''}`}>
                {sec.features.map((f, i) => {
                  // Grille à nombre IMPAIR de features (>1) : le dernier item
                  // resterait seul sur une demi-ligne (colonne vide à droite).
                  // On l'étale sur les 2 colonnes pour occuper toute la largeur.
                  const spanFull =
                    sec.features.length > 1 &&
                    sec.features.length % 2 === 1 &&
                    i === sec.features.length - 1;
                  return (
                    <div key={f.id} className={spanFull ? 'sm:col-span-2' : ''}>
                      <div className="font-serif text-[15px] leading-[1.2] font-semibold text-charcoal mb-[5px]">
                        {f.title}</div>
                      <div className="text-[13px] text-grey-800 leading-[1.5]">
                        <FeatureBody feature={f} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>))}

          {(announcement.videoNote || announcement.videoCaption) && (
            <div className="text-center mt-[34px] mb-[12px]">
              {announcement.videoNote && (
                <p className="text-grey-600 text-[12.5px] leading-[1.5]">{announcement.videoNote}</p>
              )}
              {announcement.videoCaption && (
                <p className="italic font-semibold text-charcoal text-[14px] leading-[1.4] mt-[8px] first:mt-0">
                  {announcement.videoCaption}</p>
              )}
            </div>
          )}

          {/* On monte le lecteur dès qu'une invitation vidéo (`videoCaption`)
              est posée, même sans URL encore : la façade affiche alors l'état
              natif « bientôt disponible » jusqu'à ce que `video`/`videoHref`
              soit renseigné. */}
          {(announcement.video || announcement.videoHref || announcement.videoCaption) && (
            <VideoEmbed video={announcement.video} href={announcement.videoHref} />
          )}
        </div>
      ) : (
        <div ref={bodyRef} className="overflow-auto px-12 pt-[2px] pb-[14px]">
          {announcement.sections.map((sec) => (
            <section key={sec.label}>
              {/* v8 : 13px d'air entre blocs (vs 26px v7) — le 8px du
                  premier label v8 est lissé à 13px ici, écart négligeable. */}
              <div className="text-[9.5px] font-bold uppercase tracking-[0.12em] text-grey-400 mt-[13px] mb-0">
                {sec.label}</div>
              {sec.features.map((f) => (
                <FeatureRow key={f.id} feature={f} isOpen={openIds.has(f.id)} onToggle={() => toggle(f.id)} />
              ))}
            </section>))}
        </div>
      )}

      {announcement.footer && (
        <footer className="px-12 py-[9px] border-t border-grey-100 bg-grey-50 text-xs text-grey-400 text-center mt-auto">
          {announcement.footer.text}{' '}
          {announcement.footer.cta && (
            <a className="text-grey-600 underline" href={announcement.footer.cta.url}
               target="_blank" rel="noopener noreferrer">{announcement.footer.cta.label}</a>)}
        </footer>)}
    </div>
  );
}

/**
 * Modale d'annonce produit — 100 % présentielle (une seule annonce, push login).
 *
 * Aucun hook de données (pas de QueryClientProvider requis) : tout vient des props.
 *
 * Token adaptations vs. mockup v7 (voir tailwind.config.ts) :
 * - `font-crimson` n'existe pas → famille Crimson Text exposée via `font-serif`.
 * - `text-grey-700` / `text-grey-300` n'existent pas → échelle DS
 *   {50,100,200,400,600,800,900} ; corps de texte sur `grey-800`, chevron sur `grey-400`.
 *
 * Les hex sur-mesure or/bleu (`#B8860B` / `#8a6400` / `#1f3d6b`) reproduisent
 * volontairement la charte du mockup validé « v11 » et non les tokens
 * `accent-gold` / `accent-blue` — ne pas les « corriger » à l'aveugle.
 */
export function AnnouncementModal({ announcement, open, onClose }: Props) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent hideCloseButton
        className="max-w-[1080px] w-[94vw] max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden rounded-[14px]">
        <DialogTitle className="sr-only">{announcement.title}</DialogTitle>

        <button onClick={onClose} aria-label="Fermer"
          className="absolute top-4 right-[18px] h-7 w-7 flex items-center justify-center
                     text-[24px] leading-none text-grey-400 hover:text-charcoal transition-colors z-10">
          &times;
        </button>

        {/* key = remount au changement d'annonce → reset openIds + verrou hauteur */}
        <AnnouncementContent key={announcement.id} announcement={announcement} onCtaClick={onClose} />
      </DialogContent>
    </Dialog>);
}
